// KL guide: exercise during and after pantang (confinement). Medical content
// is general guidance and always defers to the mother's doctor.
const content = {
    slug: 'pantang-confinement-exercise',
    title: 'Exercise During and After Pantang | Confinement Guide, Malaysia',
    description: 'What exercise is safe during pantang (confinement) in Malaysia, when to start training again, and a gentle step-by-step return after birth.',
    eyebrow: 'KL · Fitness guide',
    h1: 'Exercise During and After Pantang: A Gentle Guide',
    lead: 'In Malaysia, the weeks after birth are traditionally a time of rest - pantang for Malay families, confinement for Chinese families, and similar practices in Indian households. Rest matters. But a few gentle exercises can help recovery, and knowing when to start properly makes the return to fitness smoother.',
    datePublished: '2026-10-03',
    image: 'personal-trainer/mother-with-her-baby-daughter-practice-yoga-home.jpg',
    imageAlt: 'Mother doing gentle exercise at home with her baby',
    summary: {
        title: 'In short',
        items: [
            'Pantang commonly lasts around 30 to 44 days.',
            'During it: rest, breathing and gentle pelvic floor exercises (with your doctor\'s OK).',
            'Structured training usually starts after pantang and your postnatal check.',
            'After a caesarean, wait for your doctor\'s clearance - often later.',
        ],
    },
    sections: [
        {
            title: 'During pantang: what is usually fine',
            items: [
                'Deep breathing - slow breaths into the belly and ribs, which helps the deep core reconnect.',
                'Pelvic floor squeezes (Kegel exercises) - short holds, a few times a day, if comfortable.',
                'Short, easy walks around the house as you feel able.',
                'Gentle stretches for neck and shoulders, which get stiff from feeding.',
            ],
            note: 'Every birth is different. Follow your doctor\'s or midwife\'s advice, and stop any exercise that causes pain or bleeding.',
        },
        {
            title: 'What to avoid in the first weeks',
            items: [
                'Sit-ups, crunches and heavy lifting.',
                'High-impact exercise like running or jumping.',
                'Anything that causes pain, heaviness in the pelvis or increased bleeding.',
            ],
        },
        {
            title: 'After pantang: a step-by-step return',
            steps: [
                { title: 'Weeks 6-8', body: 'After your postnatal check: breathing, pelvic floor and gentle deep core work. Longer walks.' },
                { title: 'Weeks 8-12', body: 'Light strength - squats to a chair, glute bridges, supported rows - and checking for abdominal separation.' },
                { title: 'Months 3-6', body: 'Progressive strength training and low-impact cardio. Return to running only when your pelvic floor feels strong.' },
                { title: 'After 6 months', body: 'Most women can return to their previous training, built back up gradually.' },
            ],
        },
        {
            title: 'Balancing tradition and exercise',
            paragraphs: [
                'Many families follow traditional practices such as warm drinks, herbal baths and staying indoors during pantang. Gentle breathing and pelvic floor work fit comfortably alongside them - they can be done lying down at home. If elders in the family are worried about exercise, explain that these exercises are about recovery, not workouts.',
            ],
        },
    ],
    faqs: [
        {
            question: 'Can I exercise during pantang?',
            answer: 'Rest comes first, but breathing and gentle pelvic floor exercises are usually fine early on with your doctor\'s agreement.',
        },
        {
            question: 'When can I start exercising after a caesarean?',
            answer: 'Usually later than after a vaginal birth - wait for your doctor\'s clearance, often around eight weeks, and build up slowly.',
        },
    ],
    cta: {
        title: 'Ready to start after pantang?',
        body: 'A postnatal trainer rebuilds your core and strength step by step, at home.',
        href: '/my/personal-trainer-kuala-lumpur/prenatal-postnatal-personal-trainer',
        label: 'Postnatal personal training',
    },
}

export default content
